import { Building2, MoreVertical, Trash2, Edit2, Star } from "lucide-react";
import { useState } from "react";

export interface BankAccount {
  id: string;
  bankName: string;
  bankCode: string;
  accountType: "checking" | "savings";
  agency: string;
  accountNumber: string;
  accountHolderName: string;
  cpf: string;
  isDefault: boolean;
}

interface BankAccountItemProps {
  account: BankAccount;
  onEdit: (account: BankAccount) => void;
  onDelete: (id: string) => void;
  onSetDefault: (id: string) => void;
}

export function BankAccountItem({
  account,
  onEdit,
  onDelete,
  onSetDefault,
}: BankAccountItemProps) {
  const [showMenu, setShowMenu] = useState(false);

  const getAccountTypeName = () => {
    return account.accountType === "checking" ? "Conta Corrente" : "Conta Poupança";
  };

  const maskCPF = (cpf: string) => {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.***.**$4");
  };

  return (
    <div className="relative bg-card rounded-xl shadow-sm border border-border p-4">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
          <Building2 className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium">{account.bankName}</h3>
            {account.isDefault && (
              <div className="flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded">
                <Star className="w-3 h-3 fill-current" />
                Padrão
              </div>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-1">
            {account.accountHolderName}
          </p>
          <div className="space-y-0.5 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <span>{getAccountTypeName()}</span>
              <span>•</span>
              <span>
                Ag: {account.agency} / Conta: {account.accountNumber}
              </span>
            </div>
            <div>CPF: {maskCPF(account.cpf)}</div>
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
            aria-label="Opções"
          >
            <MoreVertical className="w-5 h-5" />
          </button>

          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowMenu(false)}
              />
              <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden">
                {!account.isDefault && (
                  <button
                    onClick={() => {
                      onSetDefault(account.id);
                      setShowMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent transition-colors text-left"
                  >
                    <Star className="w-4 h-4" />
                    <span className="text-sm">Tornar padrão</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    onEdit(account);
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent transition-colors text-left"
                >
                  <Edit2 className="w-4 h-4" />
                  <span className="text-sm">Editar</span>
                </button>
                <button
                  onClick={() => {
                    onDelete(account.id);
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent transition-colors text-left text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="text-sm">Remover</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
